import React, { useState, useRef, useEffect } from 'react';
import InventoryService from '../../../services/InventoryService';
import analytics from '../../../services/AnalyticsService';
import { useFilePicker } from '../../../hooks/useFilePicker';

// Per-slot caps must match the backend (api/items.py IMAGE_TYPE_LIMITS).
// `kind` maps to useFilePicker's canonical accept strings. The Item Photos
// slot is image-only (gets rendered in the item gallery carousel); the
// document slots accept PDFs as well.
const SLOTS = [
    {
        key: 'photo',
        label: 'Item Photos',
        max: 3,
        helper: 'Best for color, finish, condition',
        kind: 'image',
        acceptText: 'PNG, JPG, WEBP',
    },
    {
        key: 'receipt',
        label: 'Receipt',
        max: 2,
        helper: 'Best for purchase date & price',
        kind: 'image-or-pdf',
        acceptText: 'PNG, JPG, PDF',
    },
    {
        key: 'manual',
        label: 'Manual / Spec Sheet',
        max: 1,
        helper: 'Best for warranty & specs',
        kind: 'image-or-pdf',
        acceptText: 'PNG, JPG, PDF',
    },
    {
        key: 'tag',
        label: "Manufacturer's Tag",
        max: 1,
        helper: 'Best for serial & model number',
        kind: 'image-or-pdf',
        acceptText: 'PNG, JPG, PDF',
    },
];

const isImageFile = (file) => file && file.type && file.type.startsWith('image/');
const isPdfFile = (file) => file && file.type === 'application/pdf';
const isAcceptableForSlot = (slotKey, file) => {
    if (!file) return false;
    if (slotKey === 'photo') return isImageFile(file);
    return isImageFile(file) || isPdfFile(file);
};

/**
 * One slot block. Extracted so each slot mounts its own useFilePicker hook;
 * a single useFilePicker call wouldn't work because each slot has a
 * different kind (image vs image-or-pdf) and capacity.
 */
const SlotBlock = ({ slot, files, isScanning, getPreviewUrl, onAdd, onRemove }) => {
    const isFull = files.length >= slot.max;

    const { triggerPicker, fileInputElement } = useFilePicker({
        kind: slot.kind,
        multiple: slot.max > 1,
        // Match the backend's 10MB per-file scan cap and stay consistent with
        // ItemImageGallery's slot picker — same data, same limit.
        maxSizeMB: 10,
        onFiles: (picked) => onAdd(slot.key, picked),
        disabled: isFull || isScanning,
        sheetTitle: `Add ${slot.label}`,
    });

    return (
        <div className="border border-[var(--border)] rounded-2xl p-4 bg-[color-mix(in_srgb,var(--bg-hover),transparent_60%)]">
            <div className="flex items-baseline justify-between mb-1">
                <span className="font-semibold text-sm text-[var(--primary)]">{slot.label}</span>
                <span className="text-xs text-[var(--text-muted)]">{files.length}/{slot.max}</span>
            </div>
            <p className="text-xs text-[var(--text-muted)] mb-3">{slot.helper}</p>

            {fileInputElement}

            {files.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                    {files.map((f, i) => {
                        const previewUrl = getPreviewUrl(f);
                        return (
                            <div key={i} className="flex flex-col items-center gap-1 w-14">
                                <div className="relative w-14 h-14 rounded-lg border border-[var(--border)] bg-[var(--bg-card)] flex items-center justify-center overflow-hidden">
                                    {previewUrl ? (
                                        <img src={previewUrl} alt={f.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="text-[10px] font-bold text-[var(--text-muted)]">PDF</span>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => onRemove(slot.key, i)}
                                        aria-label={`Remove ${f.name}`}
                                        className="absolute -top-1.5 -right-1.5 bg-[var(--error)] text-[var(--error-container)] rounded-full w-4 h-4 flex items-center justify-center text-[10px] shadow"
                                    >
                                        &times;
                                    </button>
                                </div>
                                <span
                                    className="text-[9px] text-[var(--text-muted)] truncate w-14 text-center"
                                    title={f.name}
                                >
                                    {f.name}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}

            <button
                type="button"
                disabled={isFull || isScanning}
                onClick={triggerPicker}
                className="w-full py-2 text-xs font-bold rounded-lg border border-dashed border-[var(--border-visible)] text-[var(--primary)] hover:bg-[var(--bg-card)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
                {isFull ? 'Slot Full' : `+ Add ${slot.label}`}
            </button>
            <p className="text-[10px] text-[var(--text-muted)] mt-2 text-center">{slot.acceptText}</p>
        </div>
    );
};

const SmartAutofillSlots = ({ onScanSuccess }) => {
    // slotFiles: { photo: File[], receipt: File[], manual: File[], tag: File[] }
    const [slotFiles, setSlotFiles] = useState({ photo: [], receipt: [], manual: [], tag: [] });
    const [isScanning, setIsScanning] = useState(false);
    const [error, setError] = useState(null);

    // Stable per-File object URL cache. File objects are reference-stable
    // across renders, so keying on the File itself means we don't recreate
    // (and flash) the preview for files that haven't changed.
    const urlCacheRef = useRef(new Map());

    const getPreviewUrl = (file) => {
        if (!isImageFile(file)) return null;
        const cache = urlCacheRef.current;
        if (!cache.has(file)) {
            cache.set(file, URL.createObjectURL(file));
        }
        return cache.get(file);
    };

    // Revoke URLs for files that left the active set. Runs whenever slotFiles
    // changes; final cleanup on unmount revokes everything.
    useEffect(() => {
        const active = new Set(
            Object.values(slotFiles).flat()
        );
        const cache = urlCacheRef.current;
        for (const [file, url] of cache.entries()) {
            if (!active.has(file)) {
                URL.revokeObjectURL(url);
                cache.delete(file);
            }
        }
    }, [slotFiles]);

    useEffect(() => {
        const cache = urlCacheRef.current;
        return () => {
            for (const url of cache.values()) URL.revokeObjectURL(url);
            cache.clear();
        };
    }, []);

    // Forward all currently-staged files up to the parent so the save handler
    // can upload each one with its correct image_type after the item is saved.
    const reportToParent = (next, scanData = null) => {
        const flat = [];
        for (const slot of SLOTS) {
            for (const file of next[slot.key] || []) {
                flat.push({ slot: slot.key, file });
            }
        }
        onScanSuccess(scanData || {}, flat);
    };

    const addFilesToSlot = (slotKey, files) => {
        const slot = SLOTS.find(s => s.key === slotKey);
        if (!slot) return;
        const accepted = Array.from(files).filter(f => isAcceptableForSlot(slotKey, f));
        if (accepted.length === 0) {
            setError(`Unsupported file for ${slot.label}. Allowed: ${slot.acceptText}.`);
            return;
        }
        setError(null);
        setSlotFiles(prev => {
            const existing = prev[slotKey] || [];
            const remainingSpace = slot.max - existing.length;
            const merged = [...existing, ...accepted.slice(0, remainingSpace)];
            const next = { ...prev, [slotKey]: merged };
            reportToParent(next);
            if (accepted.length > remainingSpace) {
                setError(`${slot.label} is full (max ${slot.max}). Some files were skipped.`);
            }
            return next;
        });
    };

    const removeFile = (slotKey, index) => {
        setSlotFiles(prev => {
            const next = { ...prev, [slotKey]: prev[slotKey].filter((_, i) => i !== index) };
            reportToParent(next);
            return next;
        });
    };

    const totalFiles = Object.values(slotFiles).reduce((acc, arr) => acc + arr.length, 0);

    const handleScan = async () => {
        if (totalFiles === 0) return;
        setIsScanning(true);
        setError(null);

        const flat = [];
        const slotBreakdown = { photo: 0, receipt: 0, manual: 0, tag: 0 };
        const fileSizesKb = [];
        const fileMimeTypes = [];
        for (const slot of SLOTS) {
            for (const file of slotFiles[slot.key] || []) {
                flat.push({ slot: slot.key, file });
                slotBreakdown[slot.key] += 1;
                fileSizesKb.push(Math.round((file.size || 0) / 1024));
                fileMimeTypes.push(file.type || 'unknown');
            }
        }

        analytics.track('inventory.scan_initiated', {
            app_flow: 'add_asset',
            slot_count: flat.length,
            slot_breakdown: slotBreakdown,
            file_sizes_kb: fileSizesKb,
            file_mime_types: fileMimeTypes,
        });

        const startedAt = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        try {
            const response = await InventoryService.smartScanMultiple(flat);
            const durationMs = Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now())) - startedAt);
            const item = (response && response.items && response.items[0]) || null;
            const fieldsExtracted = item ? Object.keys(item).filter(k => item[k] !== null && item[k] !== '' && item[k] !== undefined).length : 0;

            if (item) {
                analytics.track('inventory.scan_completed', {
                    app_flow: 'add_asset',
                    success: true,
                    duration_ms: durationMs,
                    items_extracted_count: response.items.length,
                    fields_extracted_count: fieldsExtracted,
                    item_category: item.item_category || item.category,
                    item_subcategory: item.item_subcategory,
                    manufacturer: item.manufacturer || item.brand,
                });
                reportToParent(slotFiles, item);
            } else {
                analytics.track('inventory.scan_completed', {
                    app_flow: 'add_asset',
                    success: false,
                    duration_ms: durationMs,
                    items_extracted_count: 0,
                    fields_extracted_count: 0,
                    error_code: 'no_items_extracted',
                });
                setError('Could not extract details. You can still enter them manually.');
                reportToParent(slotFiles);
            }
        } catch (err) {
            console.error('Multi-file scan error', err);
            const durationMs = Math.round(((typeof performance !== 'undefined' ? performance.now() : Date.now())) - startedAt);
            const errorCode = err?.response?.data?.error_code || (err?.response?.status ? `http_${err.response.status}` : 'network_error');
            analytics.track('inventory.scan_completed', {
                app_flow: 'add_asset',
                success: false,
                duration_ms: durationMs,
                items_extracted_count: 0,
                fields_extracted_count: 0,
                error_code: errorCode,
            });
            setError('Failed to scan. You can still save the asset and the files will upload.');
            reportToParent(slotFiles);
        } finally {
            setIsScanning(false);
        }
    };

    return (
        <div className="w-full">
            <div className="bg-[var(--bg-card)] p-6 md:p-8 rounded-3xl shadow-[var(--shadow)] border border-[var(--border)]">
                <div className="mb-6">
                    <h3 className="text-xl font-bold text-[var(--primary)] font-['Manrope'] mb-2">Smart Autofill</h3>
                    <p className="text-[var(--text-secondary)] text-sm leading-relaxed">
                        Upload one or more files into the right slots. Each slot helps the AI extract different details — combine them for the most complete autofill.
                    </p>
                </div>

                {/* Single-column list instead of a 2-up grid: this component lives
                    in the lg:flex-[1] companion panel of AddAsset, which is too
                    narrow for two slot tiles side-by-side without word-wrap on
                    the longer labels ("Manual / Spec Sheet", "Manufacturer's Tag"). */}
                <div className="flex flex-col gap-3">
                    {SLOTS.map(slot => (
                        <SlotBlock
                            key={slot.key}
                            slot={slot}
                            files={slotFiles[slot.key] || []}
                            isScanning={isScanning}
                            getPreviewUrl={getPreviewUrl}
                            onAdd={addFilesToSlot}
                            onRemove={removeFile}
                        />
                    ))}
                </div>

                {error && (
                    <p className="text-[var(--error)] text-sm mt-4 text-center">{error}</p>
                )}

                <button
                    type="button"
                    onClick={handleScan}
                    disabled={totalFiles === 0 || isScanning}
                    className="mt-6 w-full py-4 bg-[var(--primary)] text-[var(--primary-text)] rounded-full font-bold text-sm hover:bg-[var(--primary-container)] transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {isScanning ? (
                        <>
                            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--primary-text)]" />
                            Scanning {totalFiles} file{totalFiles === 1 ? '' : 's'}...
                        </>
                    ) : (
                        `Autofill from ${totalFiles} file${totalFiles === 1 ? '' : 's'}`
                    )}
                </button>
                <p className="text-[10px] text-[var(--text-muted)] mt-2 text-center">
                    Counts as 1 scan regardless of file count
                </p>
            </div>
        </div>
    );
};

export default SmartAutofillSlots;
